import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type TemplateKind, type TemplateSummary, useTemplates } from '@/entities/template';
import { FormError } from '@/shared/forms';
import { Dialog, EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import styles from './Templates.module.css';

/**
 * A compact searchable list of the viewer's own templates of one kind. Choosing one calls
 * `onPick`; the dialog stays open while that runs, so a failure can be shown here.
 */
export function TemplatePickerDialog({
  kind,
  onPick,
  pending = false,
  error,
  onClose,
}: {
  kind: TemplateKind;
  onPick: (template: TemplateSummary) => void;
  pending?: boolean;
  error?: unknown;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const templates = useTemplates(kind);
  const [text, setText] = useState('');
  const needle = text.trim().toLocaleLowerCase();
  const shown = (templates.data ?? []).filter(
    (item) =>
      needle === '' ||
      item.name.toLocaleLowerCase().includes(needle) ||
      item.headline.toLocaleLowerCase().includes(needle),
  );

  return (
    <Dialog title={t(`templates.pick.title.${kind}`)} onClose={onClose}>
      <div className={styles.picker}>
        <input
          type="search"
          className={styles.search}
          aria-label={t('templates.pick.search')}
          placeholder={t('templates.pick.search')}
          value={text}
          autoFocus
          maxLength={100}
          onChange={(event) => {
            setText(event.target.value);
          }}
        />
        {templates.isPending ? (
          <LoadingState />
        ) : templates.isError ? (
          <ErrorState
            error={templates.error}
            onRetry={() => {
              void templates.refetch();
            }}
          />
        ) : shown.length === 0 ? (
          <EmptyState title={t(needle ? 'templates.pick.none' : `templates.pick.empty.${kind}`)} />
        ) : (
          <ul className={styles.list}>
            {shown.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={styles.item}
                  disabled={pending}
                  onClick={() => {
                    onPick(item);
                  }}
                >
                  <span className={styles.itemName}>{item.name}</span>
                  <span className={styles.itemMeta}>{item.headline}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <FormError error={error} />
      </div>
    </Dialog>
  );
}
